import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Download, Upload, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e) => e?.response?.data?.detail || "حدث خطأ";

const download = async () => {
  try {
    const r = await axios.get(`${API}/keeta/link-template`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "ربط_مناديب_كيتا.xlsx";
    a.click();
  } catch (e) { toast.error("تعذّر تحميل النموذج"); }
};

const Preview = ({ data }) => (
  <div className="max-h-[50vh] overflow-auto rounded-xl border">
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-slate-50 text-slate-500"><tr><th className="p-2 text-right">معرف كيتا</th><th className="p-2 text-right">اسم المندوب</th><th className="p-2 text-right">الإقامة / الرقم</th><th className="p-2 text-right">الموظف</th></tr></thead>
      <tbody>{data.rows.map((r, i) => (
        <tr key={i} className={`border-t ${r.employee_id ? "" : "bg-red-50"}`} data-testid={`klink-row-${i}`}>
          <td className="p-2 font-mono" dir="ltr">{r.rider_id}</td><td className="p-2">{r.rider_name}</td><td className="p-2 font-mono" dir="ltr">{r.key}</td>
          <td className={`p-2 font-semibold ${r.employee_id ? "text-emerald-700" : "text-red-600"}`}>{r.employee_name || "غير موجود"}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const KeetaLinkExcel = ({ onDone }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { setData((await axios.post(`${API}/keeta/link-preview`, fd)).data); } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  const apply = async () => {
    const items = data.rows.filter((r) => r.employee_id).map((r) => ({ rider_id: r.rider_id, employee_id: r.employee_id }));
    setBusy(true);
    try {
      const r = await axios.post(`${API}/keeta/link-apply`, { items });
      toast.success(`تم ربط ${r.data.linked} مندوب`); setData(null); setOpen(false); onDone?.();
    } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="klink-open-btn"><Link2 className="h-4 w-4" /> ربط مناديب كيتا</Button>
      <input ref={ref} type="file" hidden accept=".xlsx,.xls" data-testid="klink-file-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <Dialog open={open} onOpenChange={(o) => !busy && (setOpen(o), o || setData(null))}>
        <DialogContent dir="rtl" className="sm:max-w-3xl font-body" data-testid="klink-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading">ربط مناديب كيتا بالموظفين</DialogTitle>
            <DialogDescription>١) حمّل النموذج (فيه كل مناديب كيتا غير المربوطين) ٢) اكتب أمام كل مندوب رقم الإقامة أو الرقم الوظيفي ٣) ارفعه وراجع المعاينة ثم أكّد. تُضاف منصة «كيتا» لملف الموظف تلقائياً.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-1.5" onClick={download} data-testid="klink-template-btn"><Download className="h-4 w-4" /> تحميل النموذج</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => ref.current?.click()} disabled={busy} data-testid="klink-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع الملف المعبّأ</Button>
          </div>
          {data && (
            <div className="space-y-3">
              <p className="text-sm" data-testid="klink-summary"><span className="font-bold text-emerald-700">{data.matched} مطابق</span> · <span className="font-bold text-red-600">{data.unmatched} غير مطابق</span> (رقم إقامة/وظيفي غير موجود)</p>
              <Preview data={data} />
              <Button onClick={apply} disabled={busy || !data.matched} className="bg-emerald-600 hover:bg-emerald-700" data-testid="klink-apply-btn">تأكيد ربط {data.matched} مندوب</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
